import type { CategoryContent } from './row.js';

export const APPLIED_PHYSICS: CategoryContent = {
  bscba: [
    [
      'When light travels from glass into air, what physically happens at the critical angle?',
      [
        'The refracted ray grazes the interface at 90°',
        'The light undergoes partial reflection and partial refraction',
        'The wavelength decreases as the ray exits the glass',
        'Absorption increases because the photons lose energy',
      ],
      0,
      'At the critical angle, Snell\'s law yields a refraction angle of exactly 90°, so the ray emerges parallel to the interface.',
    ],
    [
      'What region forms at the boundary when p-type and n-type semiconductor materials are brought into contact?',
      [
        'A depletion region with a built-in electric field',
        'A conducting layer that short-circuits across the junction',
        'A thermal barrier that prevents any current flow',
        'A metallic interface with zero resistance',
      ],
      0,
      'Diffusion of majority carriers creates charge separation and a built-in electric field that stops further diffusion at equilibrium.',
    ],
    [
      'In fluid mechanics, Bernoulli\'s equation assumes conservation of which quantity along a streamline?',
      [
        'Mechanical energy per unit volume',
        'Angular momentum of the fluid',
        'Entropy at constant temperature',
        'Chemical potential of dissolved ions',
      ],
      0,
      'Bernoulli\'s equation is a form of energy conservation: pressure energy, kinetic energy, and gravitational potential energy sum to a constant.',
    ],
    [
      'What property of a material is the product of its density and the speed of sound within it?',
      [
        'Acoustic impedance',
        'Acoustic attenuation coefficient',
        'Acoustic resonance frequency',
        'Acoustic dispersion parameter',
      ],
      0,
      'Acoustic impedance (Z = ρc) determines reflection and transmission of sound waves at material boundaries.',
    ],
    [
      'How are X-rays produced in a clinical tube?',
      [
        'Electrons strike a metal target and emit photons',
        'Ionized atoms in a gas emit characteristic radiation',
        'Radioactive decay of tungsten nuclei in the tube',
        'Friction between rotating electrodes generates electromagnetic radiation',
      ],
      0,
      'Electrons accelerated across a potential strike the anode; deceleration radiates energy as X-rays (Bremsstrahlung) or characteristic photons.',
    ],
    [
      'What is the primary mechanism of heat transfer through a solid metal rod held between two heat sources?',
      [
        'Conduction via phonons and free electrons',
        'Convection as metal atoms physically move through the rod',
        'Radiation from thermal vibrations inside the metal lattice',
        'Phase change at the interface between hot and cold regions',
      ],
      0,
      'In metals, conduction occurs through phonons (lattice vibrations) and electron transport; convection and radiation are negligible in solids.',
    ],
    [
      'A laser beam is characterized by coherence. What does coherence mean in this context?',
      [
        'Waves maintain a constant phase relationship',
        'The beam travels in a perfectly straight line without divergence',
        'All photons are emitted simultaneously in a single burst',
        'The beam intensity is uniform across its entire cross-section',
      ],
      0,
      'Coherence means waves are in a stable phase relationship—coherent light from a laser interferes constructively, unlike incoherent sources.',
    ],
    [
      'The continuity equation in fluid dynamics states that if a fluid flows through a pipe that narrows, what must increase?',
      [
        'The velocity of the fluid',
        'The pressure inside the pipe',
        'The viscosity of the fluid',
        'The density at constant temperature',
      ],
      0,
      'Continuity equation: ρAv = constant (mass flow rate conserved). Smaller area A requires larger velocity v at constant density.',
    ],
    [
      'Why does sound travel faster through water than through air at the same temperature?',
      [
        'Water\'s bulk modulus enables faster stress waves',
        'Water has more oxygen molecules that vibrate faster',
        'Sound waves in water are electromagnetic rather than mechanical',
        'Salinity in water increases the speed of acoustic waves',
      ],
      0,
      'Sound speed is c = √(K/ρ), where K is bulk modulus. Water\'s higher bulk modulus outweighs its higher density.',
    ],
    [
      'In a forward-biased p-n junction diode, what causes current to flow across the junction?',
      [
        'External voltage narrows the depletion region',
        'Electrons and holes are created by thermal energy inside the junction',
        'The depletion region becomes thicker and conducts more easily',
        'Tunneling through the depletion region occurs at room temperature',
      ],
      0,
      'Forward bias shrinks the depletion region, lowering the barrier so majority carriers diffuse across and exceed drift current.',
    ],
    [
      'Fiber optics relies on light traveling through the core due to repeated total internal reflection. Why not use a mirror-coated hollow tube instead?',
      [
        'Fiber guides light with much lower loss than mirrors',
        'Mirror tubes require vacuum, while fiber works in any environment',
        'Light travels faster through solid glass than in air or vacuum',
        'Fiber is cheaper to manufacture at scale',
      ],
      0,
      'Optical fibers achieve low loss via total internal reflection; mirror tubes scatter, absorb, and cannot maintain beam quality over long distances.',
    ],
    [
      'In heat exchanger design, what is the primary mode of heat transfer across the thin metal wall separating two fluids?',
      [
        'Conduction through the metal wall',
        'Convection from one fluid to the wall, then to the other',
        'Radiation from one fluid surface to the other',
        'A phase change on one side that releases latent heat',
      ],
      0,
      'Primary mode is conduction through the wall; convection occurs within each fluid, but the wall itself transfers heat via conduction.',
    ],
    [
      'A car horn moves toward you at high speed. Why does its pitch sound higher than when stationary?',
      [
        'Motion compresses the wavelength, raising pitch',
        'The horn radiates more power when moving, so amplitude increases',
        'Doppler shift changes the speed of sound in air',
        'The car\'s acceleration creates a resonance cavity effect',
      ],
      0,
      'Doppler effect: source moving toward observer reduces wavelength λ = v/(f + v_source/c), raising the heard frequency.',
    ],
    [
      'A photodiode converts light to electrical current. Which quantum process enables this conversion?',
      [
        'Photons excite electrons across the band gap',
        'Light heats the material, increasing electron thermal velocity',
        'Photons directly transfer momentum to electrons without energy loss',
        'Laser light induces stimulated emission that cascades into current',
      ],
      0,
      'Photons excite carriers into the band gap; the junction field sweeps them apart before they recombine, creating measurable photocurrent.',
    ],
    [
      'In nuclear engineering, the macroscopic cross-section Σ = N·σ relates to the microscopic cross-section σ. What does N represent?',
      [
        'Number density of nuclei',
        'Number of neutrons emitted per fission',
        'Total mass number of the target nucleus',
        'Number of reactions occurring per second',
      ],
      0,
      'Macroscopic cross-section is microscopic cross-section scaled by number density; it predicts how likely a neutron will interact in the material.',
    ],
  ],
  msc: [
    [
      'Stimulated Raman scattering in a fiber-optic link becomes problematic above a certain power threshold. What physical mechanism limits amplification?',
      [
        'Nonlinear pump depletion reduces the gain',
        'Thermal expansion of the fiber core reduces the refractive index',
        'Rayleigh scattering of the Stokes wave dominates at high power',
        'Spontaneous emission in the fiber becomes incoherent and injects noise',
      ],
      0,
      'SRS is nonlinear: pump depletion becomes significant above threshold, limiting energy transfer to the Stokes wave.',
    ],
    [
      'In III-V semiconductor heterostructures used for LEDs, why is the band-gap energy of the cladding layer larger than the active layer?',
      [
        'A potential well confines carriers in the active layer',
        'Optical transparency in the cladding prevents loss of emitted photons',
        'Lattice constant mismatch requires a graded composition',
        'Thermal management: wider-gap materials conduct heat more efficiently',
      ],
      0,
      'Heterojunctions create potential barriers; wider-gap cladding confines carriers to the narrower-gap active layer, enhancing recombination there.',
    ],
    [
      'In the context of computational fluid dynamics (CFD), turbulent flow is closed by adding terms to the Navier–Stokes equations. What is this closure typically called?',
      [
        'Turbulence modeling, via eddy viscosity models',
        'Spectral decomposition of vorticity fields',
        'Time-averaging to eliminate fluctuating pressure terms',
        'Artificial diffusion to stabilize numerical schemes',
      ],
      0,
      'Turbulence closure models (k-ε, k-ω, Spalart–Allmaras) approximate the effect of unresolved scales on resolved scales via eddy viscosity.',
    ],
    [
      'In ultrasonic phased-array imaging, how does time-delay focusing enable spatial resolution in the axial direction?',
      [
        'Delays focus constructive interference at one depth',
        'Delayed signals scatter less from off-axis structures',
        'Frequency shifts from Doppler effect allow depth discrimination',
        'Acoustic impedance mismatch varies with depth, enhancing borders',
      ],
      0,
      'Phased array: transmit delays align wave fronts at chosen depth z; reflections from that depth interfere constructively, others cancel.',
    ],
    [
      'A synchrotron X-ray beamline uses a silicon(111) analyzer crystal to achieve energy resolution. What is the underlying principle?',
      [
        'The crystal has a narrow Darwin curve in energy',
        'The crystal\'s thermal vibrations are minimized, reducing recoil shift',
        'Silicon(111) planes are perfectly parallel, eliminating geometric broadening',
        'The analyzer selects photons by wavelength via the photoelectric effect',
      ],
      0,
      'Darwin curve (dynamical diffraction): only photons within a very narrow energy window satisfy the Bragg condition at the analyzer plane.',
    ],
    [
      'Why does the thermal conductivity of copper decrease as temperature increases, despite more vibrations?',
      [
        'Umklapp scattering shortens the phonon mean free path',
        'Electrons become less free as the lattice expands',
        'The Fermi level rises and reduces the density of states',
        'Copper undergoes a phase transition that reduces conduction',
      ],
      0,
      'Above the Debye temperature, Umklapp scattering dominates: phonon-phonon collisions shorten mean free path faster than T-driven improvements.',
    ],
    [
      'In externally modulated lithium-niobate Mach–Zehnder modulators, how does an applied voltage modulate the intensity of light?',
      [
        'Voltage shifts the refractive index via the Pockels effect',
        'Voltage directly ionizes trapped electrons, changing absorption',
        'Voltage induces birefringence that splits the beam',
        'Voltage shifts the band gap, altering photon-phonon coupling',
      ],
      0,
      'Pockels effect (linear electro-optic): voltage Δφ = (π/2)(V/Vπ) shifts phase; destructive/constructive interference modulates output.',
    ],
    [
      'In polymer-matrix composite materials, fiber alignment and fiber-matrix adhesion both influence stiffness. Which has the greater effect on longitudinal modulus at high fiber volume fractions?',
      [
        'Fiber alignment dominates the longitudinal modulus',
        'Adhesion is more critical; poor bonding negates the fiber\'s contribution regardless of alignment',
        'Both contribute equally because Poisson\'s ratio couples them',
        'Volume fraction alone determines modulus independent of alignment or adhesion',
      ],
      0,
      'Rule of mixtures with alignment factor: longitudinal modulus E = ηη_o ηL (Ef Vf + Em Vm). Misalignment factor (η) dominates at high Vf.',
    ],
    [
      'Acoustooptic deflectors in laser scanning systems rely on a traveling acoustic wave to diffract light. What determines the diffraction angle?',
      [
        'Higher acoustic frequency increases the deflection angle',
        'The laser wavelength relative to the acoustic wavelength',
        'The intensity of the acoustic wave, which controls how many phonons are scattered',
        'The piezoelectric coefficient of the crystals used',
      ],
      0,
      'Acoustooptic diffraction angle θ ≈ λ_light × ν_acoustic / v_sound; higher acoustic frequency increases the effective grating period.',
    ],
    [
      'In nuclear reactor neutronics, the multiplication factor k_eff > 1 indicates a diverging chain reaction. What is the physical origin of multiplication?',
      [
        'Each fission releases neutrons that trigger more fissions',
        'Neutron capture in non-fissile isotopes releases secondary neutrons',
        'Moderator thermalization converts fast neutrons into slow neutrons indefinitely',
        'Decay heat from fission products creates additional thermal neutrons',
      ],
      0,
      'k_eff is the average number of neutrons from one generation that cause fission in the next; k > 1 means exponential growth.',
    ],
    [
      'In high-performance x86 processors, thermal-aware clock throttling reduces frequency when junction temperature exceeds a threshold. Why is throttling necessary?',
      [
        'Leakage current and electromigration increase exponentially with temperature',
        'Higher temperature causes the transistors to physically expand beyond the chip boundary',
        'The power supply voltage cannot be sustained above a certain temperature',
        'Thermal runaway is prevented by reducing the supply voltage proportionally',
      ],
      0,
      'Leakage Pₗₑₐₖ ∝ exp(qEₐ/kT) and electromigration MTF ∝ exp(-ΔEₐ/kT) are exponential in T; throttling prevents failure.',
    ],
    [
      'In medical ultrasound, the acoustic streaming effect causes fluid motion even in the absence of bulk flow. What is the mechanism?',
      [
        'Radiation pressure exerts a steady nonlinear force on particles',
        'Acoustic cavitation creates bubbles that collapse and push fluid sideways',
        'The piezoelectric transducer vibrates at audio frequencies, mechanically stirring the medium',
        'Thermal gradients from absorption create convective currents',
      ],
      0,
      'Acoustic streaming: nonlinear radiation force ∝ I²/c on absorbing particles creates steady-state circulation from transient effects.',
    ],
    [
      'In a thermoacoustic engine, a temperature gradient along a stack drives acoustic oscillations. What thermodynamic cycle does this resemble?',
      [
        'Gas parcels cycle between hot and cold, resembling a Stirling engine',
        'Carnot cycle with the stack replacing a piston engine',
        'Brayton cycle in which the working fluid flows at constant pressure',
        'Otto cycle compressed into an acoustic wave instead of discrete strokes',
      ],
      0,
      'Thermoacoustic: gas parcels oscillate between hot and cold zones, undergoing pressure-temperature changes analogous to a Stirling engine.',
    ],
    [
      'In avalanche photodiodes (APDs), internal gain from impact ionization extends the useful signal range, but introduces noise. What is the primary source of excess noise?',
      [
        'Impact ionization varies randomly between carriers',
        'Thermal noise from the high reverse-bias voltage',
        'Tunneling-induced dark current that is not amplified',
        'Spontaneous emission of photons within the junction',
      ],
      0,
      'Avalanche noise: the number of secondary carriers per primary varies randomly; the excess noise factor F > M (gain) due to this stochasticity.',
    ],
    [
      'Terahertz radiation (0.1–10 THz) lies between microwaves and infrared. Why is THz generation and detection more difficult than at adjacent frequencies?',
      [
        'THz sits between quantum and wavelength engineering limits',
        'THz is strongly absorbed by water vapor at all pressures',
        'No solid-state materials exist with band gaps in the THz energy range',
        'Quantum tunneling of charge carriers prevents THz propagation in semiconductors',
      ],
      0,
      'THz gap: quantum limit E = hν rules out simple semiconductors; wavelength λ ∼ mm rules out conventional RF engineering; a technical sweet spot.',
    ],
  ],
  phd: [
    [
      'In nonlinear fiber optics, self-phase modulation (SPM) broadens a pulse spectrum while preserving energy. What is the underlying nonlinear mechanism?',
      [
        'Intensity modulates the index via χ(3) nonlinearity',
        'Stimulated Raman scattering red-shifts the spectrum while SPM compresses the pulse',
        'Two-photon absorption creates a loss gradient that stretches the pulse in time',
        'Kerr rotation of the polarization couple-mode couples orthogonal temporal modes',
      ],
      0,
      'SPM: φ_NL(t) = γ P(t) L shifts phase of the pulse; higher intensity at peak sees larger shift, creating chirp Δν ∝ dP/dt.',
    ],
    [
      'In molecular-beam epitaxy (MBE), why is the growth rate typically limited to ∼1 μm/h despite molecular-beam fluxes of 10¹⁶ cm⁻² s⁻¹?',
      [
        'Residence time and diffusion kinetics limit atom sticking',
        'The vacuum chamber cannot support higher deposition rates without excessive outgassing',
        'Thermal radiation from the substrate limits the maximum temperature',
        'The electron gun for reflection high-energy electron diffraction (RHEED) damages the surface at higher rates',
      ],
      0,
      'MBE rate is limited by residence time τᵣ and diffusion over steps; low T improves sticking, high T increases diffusion length but reduces τᵣ.',
    ],
    [
      'In direct numerical simulation (DNS) of turbulence, why is the computational cost prohibitive for high Reynolds numbers?',
      [
        'Grid must resolve the Kolmogorov length, so cost scales steeply with Re',
        'Turbulence is chaotic; accurate prediction requires exponentially longer time steps',
        'The Navier–Stokes equations become singular above a critical Reynolds number',
        'Molecular diffusion effects become dominant at high Re, requiring quantum simulation',
      ],
      0,
      'DNS cost: grid ∝ (Re d/η)³ ∝ Re^(9/4); time-stepping ∝ Re^(1/4); total ∝ Re^(11/4) ≈ Re^2.75, prohibitive beyond Re ∼ 5000.',
    ],
    [
      'In octupole-enhanced nuclear resonance fluorescence (NRF), why can nuclei with zero quadrupole moment (Q = 0) exhibit narrower resonances than dipole-only transitions?',
      [
        'Octupole transitions have longer lifetimes and no quadrupole mixing',
        'Zero quadrupole moment prevents hyperfine broadening',
        'Octupole transitions are purely magnetic, avoiding electric-field fluctuations',
        'NRF resonances sharpen because photon recoil is eliminated in the nucleus',
      ],
      0,
      'Octupole decay A₈ < A₂; resonance width Γ ∝ A increases, but absence of quadrupole mixing prevents additional broadening mechanisms.',
    ],
    [
      'In silicon photomultipliers (SiPMs), Geiger-mode avalanche photodiodes operate in parallel with integrated quench resistors. What advantage does parallel architecture provide over single-element APDs?',
      [
        'Independent pixel discharges allow proportional photon counting',
        'Parallel pixels reduce the operating voltage required for impact ionization',
        'The quench resistors prevent any afterpulsing or dark-count events',
        'Pixel cross-talk enables signal amplification beyond single-photon sensitivity',
      ],
      0,
      'SiPM: each pixel counts 1 photon; many pixels fired = proportional output; quench time τ_q per pixel is short, filling dead time.',
    ],
    [
      'In ferrofluid dynamics, the magnetic field gradient causes particle chains to form and interact. Why does the bulk viscosity increase dramatically above a critical field strength?',
      [
        'Field-induced chains form load-bearing structures that resist shear',
        'Thermal energy melts the chains, increasing molecular motion',
        'The magnetic dipole-dipole interaction becomes repulsive at high field',
        'Iron-oxide particles undergo phase separation, trapping fluid between aggregates',
      ],
      0,
      'Field-induced structures (chains, columns) rotate with the shear flow; their structure-dissociation kinetics dominate viscous stress above threshold.',
    ],
    [
      'In X-ray crystallography at synchrotrons, harmonic rejection using a Si(999) reflection suppresses higher-order contamination. Why not simply use a monochromator filter?',
      [
        'Only the 9th-order reflection satisfies Bragg at that grazing angle',
        'Si(999) is immune to thermal deformation because it is deeply bound in the lattice',
        'Harmonic filtering reduces coherence, whereas Si(999) preserves transverse coherence',
        'Only Si(999) can achieve the required photon flux at hard X-ray energies',
      ],
      0,
      'Bragg equation: 2d sinθ = nλ; Si(999) has lattice spacing d/9; high-order (n=9) reflects at grazing angle, fundamental cannot satisfy Bragg.',
    ],
    [
      'In supercritical fluid extraction (SFE), why does extraction efficiency peak at a specific density near the critical point, rather than increasing monotonically with pressure?',
      [
        'Near the critical point, small pressure changes tune density and solvation sharply',
        'Supercritical fluids undergo a phase transition at the critical point that traps solutes',
        'The critical point causes cavitation that damages the extraction vessel',
        'Entropy dominates over enthalpy above the critical point, reducing extraction',
      ],
      0,
      'Near critical point: ∂ρ/∂P diverges; solvating power (∝ density) peaks; further increase in P raises ρ but reduces residual interactions.',
    ],
    [
      'In high-harmonic generation (HHG) from atomic gases driven by intense infrared lasers, what prevents efficient generation of harmonics beyond the ionization plateau?',
      [
        'Depletion of neutral atoms limits harmonic radiation from the plasma',
        'Harmonics above the plateau have wavelengths shorter than the laser spot size, diffracting out of the generation region',
        'Quantum defect of excited states increases for higher-lying states, suppressing recombination',
        'Phase-matching breaks down because the group-velocity mismatch becomes intolerable',
      ],
      0,
      'HHG plateau: recombination of ionized atoms produces harmonics; depletion of neutral population and free-free absorption in plasma limit efficiency above plateau.',
    ],
    [
      'In seismic waveguide propagation, why do guided P-waves along a near-surface layer exhibit a turning-point structure in depth that depends on frequency?',
      [
        'Rising velocity with depth turns low frequencies deep and high ones shallow',
        'Poisson\'s ratio varies with depth, creating a dispersive medium',
        'Attenuation is frequency-dependent, preventing low frequencies from penetrating deeply',
        'The layer thickness is wavelength-dependent, confining only certain modes',
      ],
      0,
      'Seismic waveguide: v(z) increases with depth; frequency-dependent penetration depth creates a turning-point structure analogous to optical WKB propagation.',
    ],
    [
      'In quantum cascade lasers (QCLs), the optical transition occurs between closely spaced subbands in the conduction band (ΔE ∼ 100 meV), not between distant bands. Why is this design advantageous?',
      [
        'Engineered subband spacing tunes the infrared wavelength directly',
        'Closely spaced subbands reduce thermal broadening of the laser linewidth',
        'Intersubband transitions eliminate Auger recombination, extending device lifetime',
        'The laser can access wavelengths impossible with conventional semiconductors',
      ],
      0,
      'QCL: engineered subbands via quantum confinement; intersubband transitions decouple wavelength from band gap; direct current injection pumps population inversion.',
    ],
    [
      'In fractional quantum Hall effect (FQHE), composite fermions emerge as bound states of electrons and magnetic flux quanta. Why does this reduce the many-body problem?',
      [
        'Flux attachment maps the state to integer filling of composite fermions',
        'Flux quantization constrains the total number of particles, making the state unique',
        'Electron-electron interactions become negligible in the FQHE regime',
        'The FQHE state is non-degenerate, eliminating topological order complexity',
      ],
      0,
      'CF picture: attach flux quanta to electrons; ν_CF = ν/(2nν+1); reduced effective B converts FQHE at ν to integer QHE in CF picture.',
    ],
    [
      'In resonant x-ray scattering (RXS) at absorption edges, resonant enhancement of specific scattering peaks reveals charge or orbital ordering. Why are RXS spectra more sensitive to subtle ordering than non-resonant diffraction?',
      [
        'Resonance enhances scattering forbidden in non-resonant diffraction',
        'Resonant scattering eliminates multiple scattering, improving angular resolution',
        'RXS is immune to strain-induced peak broadening',
        'Coherence length diverges at resonance, allowing detection of nanoscale order',
      ],
      0,
      'RXS: resonant enhancement at absorption edge breaks non-resonant selection rules; forbidden reflections become allowed, probing site-specific order.',
    ],
    [
      'In electromagnetically induced transparency (EIT), a weak probe laser becomes transparent when a strong control laser couples two transitions of an atom. What is the underlying mechanism?',
      [
        'A dark-state superposition destructively cancels probe absorption',
        'The control laser ionizes the upper state, preventing absorption of the probe',
        'Rabi oscillations from the control laser cause rapid excited-state decay',
        'The control laser shifts the atomic resonance frequency away from the probe wavelength',
      ],
      0,
      'EIT: dark-state superposition (adiabatic eigenstate) does not couple to probe; destructive quantum interference cancels absorption via 3-level Λ system.',
    ],
    [
      'In microfluidic electrokinetic instability, a thin permselective membrane (ion-exchange layer) under high electric field exhibits vortex roll cells. What triggers the instability?',
      [
        'Ion polarization destabilizes the flow at the interface',
        'The membrane undergoes charge reversal at high voltage',
        'Joule heating causes thermal convection inside the membrane',
        'Electrophoresis of macromolecules creates a secondary flow',
      ],
      0,
      'EEKI: salt depletion in one region, enrichment in another; ∇ρ couples to electric field E, driving secondary flow; nonlinear electrokinetics amplify vortices.',
    ],
  ],
  professor: [
    [
      'In the context of laser-plasma instabilities during inertial confinement fusion (ICF) hohlraum heating, stimulated Raman scattering (SRS) and stimulated Brillouin scattering (SBS) can couple via a common ion-acoustic wave. What is the physical consequence of this coupling?',
      [
        'Coupling lowers the threshold for parametric pump depletion',
        'Coupling increases the Stokes-wave frequency shift beyond the Raman or Brillouin frequency alone',
        'Ion-acoustic waves become self-sustaining and generate electromagnetic noise',
        'The pump beam splits into two distinct refracted beams with different wavevectors',
      ],
      0,
      'SRS-SBS coupling: ion-acoustic wave catalyzes energy transfer from pump to Stokes with lower threshold; both scattering channels feed the same acoustic mode.',
    ],
    [
      'In topological photonics, valley-Hall photonic crystals break time-reversal symmetry to create one-way edge states. How does photonic analog of the valley-Hall effect preserve directionality without magnetic fields?',
      [
        'Broken symmetry gives valleys opposite Berry curvature and chirality',
        'Optical activity (natural circular dichroism) from the material breaks time-reversal symmetry',
        'Spatially varying nonlinear absorption creates an effective nonreciprocal medium',
        'The photonic band gap opens and closes periodically, confining light at the boundaries',
      ],
      0,
      'Valley Hall effect: C₃ breaking → opposite Berry curvature at K, K\'; valley-locked edge states backscatter forbidden by valley chirality conservation.',
    ],
    [
      'In advanced nuclear reactor fuel (TRISO particles), the SiC coating layer can sustain fission-product pressure and temperature gradients without failure. What atomic-scale mechanism allows SiC to retain strength to ∼1600 °C?',
      [
        'Strong covalent Si-C bonds keep point-defect mobility low even near 1600 °C',
        'SiC forms a non-crystalline protective layer that prevents fission gas diffusion',
        'The SiC crystal structure undergoes a phase transition that locks in mechanical stability',
        'Fission fragments chemically bond to the SiC lattice, strengthening it locally',
      ],
      0,
      'SiC: high kT_D, large Eₘ for dislocation creep; below ∼1600 °C, Nabarro-Herring creep is negligible; point-defect density remains low.',
    ],
    [
      'In scanning tunneling microscopy (STM) of a metal, the tunnel current I is exponentially sensitive to gap spacing z. However, on samples with electronic disorder (e.g., surface roughness of the density of states), constant-current STM exhibits apparent topography that does not match physical topography. Why does this artifact occur?',
      [
        'Feedback adjusts tip height to hold I, so it maps DOS, not real topography',
        'Thermal vibrations of the tip broaden the tunneling resonance, reducing spatial resolution',
        'The tunnel barrier height changes with sample composition, affecting exponential sensitivity',
        'Tip-sample interactions create an attractive force that dominates over electronic effects',
      ],
      0,
      'STM constant-current: I ∝ exp(z) ρ(E_F); feedback maintains I, so z tracks ρ(E_F) variations—electronic corrugation, not topographic.',
    ],
    [
      'In femtosecond laser ablation of solids, ultrashort pulses avoid thermal diffusion into the bulk. However, the ablation threshold (fluence Fₜₕ) exhibits a nonmonotonic dependence on pulse duration τ below ∼100 fs. What causes this non-monotonicity?',
      [
        'Below the electron-phonon coupling time, shorter pulses need higher fluence',
        'Below 100 fs, multi-photon ionization becomes dominant, raising the ionization threshold',
        'The laser wavelength shifts into the ultraviolet, decreasing absorption cross-section',
        'Plasma shielding prevents shorter pulses from reaching the surface',
      ],
      0,
      'Femtosecond ablation: τ < τₑₚ (electron-phonon coupling time ∼ 100 fs) → electrons heat before equilibrating; longer τ allows phonon accumulation, lowering Fₜₕ.',
    ],
    [
      'In magnetohydrodynamic (MHD) turbulence subject to a strong background magnetic field B₀, energy cascade is anisotropic: turbulent eddies preferentially elongate along B₀, creating a "pencil-shaped" spectrum in k-space. What fundamental difference in mode coupling causes this anisotropy compared to ordinary fluid turbulence?',
      [
        'Magnetic tension suppresses perpendicular motion, favoring cascade along B₀',
        'The Alfvén wave dispersion relation ω = vₐ k‖ limits cascade to parallel wavenumbers',
        'Viscous dissipation is anisotropic due to anisotropic viscosity in a magnetized plasma',
        'Magnetic reconnection creates narrow current sheets that fragment perpendicular turbulence',
      ],
      0,
      'MHD anisotropy: B₀ suppresses Alfvén wave interaction ⊥ to B; parallel cascade dominates; perpendicular modes couple weakly → pencil spectrum.',
    ],
    [
      'In supercritical water oxidation (SCWO) for hazardous-waste treatment, ionic product K_w of water near the critical point (374 °C, 22 MPa) can be up to 10⁻¹¹, far higher than 10⁻¹⁴ at 25 °C. How does this dramatic change in K_w affect reaction kinetics of organic oxidation?',
      [
        'Higher K_w raises [H⁺]/[OH⁻], boosting •OH radicals and oxidation',
        'Higher K_w decreases solubility of organic compounds, slowing oxidation',
        'The dielectric constant of water decreases near T_c, reducing ion dissociation despite higher K_w',
        'Supercritical water inhibits water-mediated radical reactions, requiring alternative pathways',
      ],
      0,
      'SCWO: supercritical H₂O has K_w ∼ 10⁻¹¹; [H⁺][OH⁻] → 10⁻¹¹ M²; •OH generation via chain reactions accelerates free-radical oxidation of organics.',
    ],
    [
      'In optical frequency combs from mode-locked femtosecond lasers, the frequency-comb teeth are spaced by Δfᵣₑₚ (repetition rate) and offset by f_CEO (carrier-envelope-offset frequency). Why is stabilizing f_CEO critical for broadband spectroscopy applications, while Δfᵣₑₚ can be comparatively loose?',
      [
        'f_CEO sets absolute comb-tooth frequency; Δfᵣₑₚ only sets spacing',
        'f_CEO determines the laser power; Δfᵣₑₚ does not affect output intensity',
        'f_CEO locks the spectrum to optical resonances; Δfᵣₑₚ is determined by cavity length automatically',
        'Stabilizing Δfᵣₑₚ is impossible; only f_CEO can be controlled electronically',
      ],
      0,
      'f_CEO: tooth m has frequency fₘ = m·Δfᵣₑₚ + f_CEO; f_CEO drift → all teeth shift in abs. freq., breaking coherence. Δfᵣₑₚ drift → spacing error, minor for broadband.',
    ],
    [
      'In plasmonic metamaterials designed with sub-wavelength features, the effective magnetic permeability μ_eff can be negative (μ_eff < 0) even though individual metal constituents are non-magnetic. What is the origin of this effective magnetism?',
      [
        'Circulating currents in split-ring resonators give a resonant magnetic response',
        'Free electrons in the metal acquire orbital angular momentum, mimicking intrinsic spin magnetism',
        'The plasma frequency of the metal shifts below the operational frequency, reversing the permeability sign',
        'Nonlinear effects at high field strength induce permanent magnetization',
      ],
      0,
      'Plasmonic metamaterial: split-ring resonators couple magnetic field → circulating current → μ_eff < 0 near resonance; effective LC resonance dominates.',
    ],
    [
      'In cryogenic neutron detection using superfluid ⁴He (superfluid-He target), prompt nuclear recoils ionize the superfluid, generating quasiparticle excitations observable via bolometer. Why does this approach achieve sensitivity to low-mass dark matter candidates while conventional semiconductors cannot?',
      [
        'Superfluid He\'s tiny ionization energy catches recoils eV band gaps miss',
        'Superfluid He has higher density than semiconductors, increasing cross-section',
        'Helium is insensitive to photon backgrounds that plague semiconductor detectors',
        'Cryogenic bolometer response is inherently faster than semiconductor electronics',
      ],
      0,
      'Superfluid ⁴He: ionization Eᵢ ∼ meV; low-mass WIMP recoils (Eᵣ ∼ keV) produce detectable quasiparticles; Ge/Si band gap (eV) misses low-mass recoils.',
    ],
    [
      'In resonant inelastic X-ray scattering (RIXS) at 3d transition-metal K-edges, the intermediate state is a 1s core hole coupled to the 3d valence electrons. Why does RIXS access excitations (e.g., d-d transitions, magnons) that are dipole-forbidden in conventional spectroscopy?',
      [
        'Resonance allows multipole transitions forbidden by ground-state dipole rules',
        'RIXS provides momentum resolution, allowing scattering vectors that bypass dipole rules',
        'The 1s core hole screening changes the effective nuclear charge seen by 3d electrons, enabling new transitions',
        'RIXS excitation wavelength can be tuned to resonantly enhance dipole-forbidden lines',
      ],
      0,
      'RIXS: intermediate 1s-core-hole state allows quadrupole/higher-multipole couplings; final excitations from this state need not obey ground-state dipole rules.',
    ],
    [
      'In high-temperature superconductors (cuprates), the pseudogap (PG) opens below T* and suppresses spectral weight at the Fermi level, yet the superconducting gap opens below T_c < T*. What is the relationship between PG and superconducting pairing, and why are they distinct phenomena?',
      [
        'PG is a bosonic fluctuation scale above T_c; SC gap is a fermionic gap below T_c',
        'PG and SC gap are the same phenomenon; T_c measures when pair coherence onsets, while T* measures when pairing starts',
        'PG is a feature of the density of states; SC gap is a transport phenomenon',
        'PG arises from phonon screening; SC gap arises from electron-electron interactions',
      ],
      0,
      'Cuprate pseudogap: bosonic (PG ∝ spin fluctuations) above T_c, survives to T*; SC gap (fermionic, superconducting coherence) appears below T_c < T*.',
    ],
    [
      'In accretion-disk radiation transfer, the vertical structure satisfies hydrostatic equilibrium and radiative energy balance. At high accretion rates (super-Eddington), the disk becomes geometrically thick. What causes the disk to "puff up," and why does this limit the local radiative efficiency?',
      [
        'Radiation pressure dominates gravity, puffing up the disk and trapping radiation',
        'Magnetic instability (MRI) creates turbulent heating that puffs the disk',
        'Thermal conduction vertically transports energy, reducing the local temperature gradient',
        'At super-Eddington rates, nuclear burning ignites in the disk, providing additional support',
      ],
      0,
      'Super-Eddington accretion: P_rad ∼ P_grav → thick disk; trapped radiation increases effective opacity; local radiative efficiency η_rad < thin-disk limit.',
    ],
    [
      'In time-resolved X-ray diffraction (TR-XRD) during femtosecond laser-induced phase transitions, lattice dynamics are accessed via time-resolved changes in diffraction peak intensity and broadening. What information about the ultrafast phonon dynamics is encoded in the time-dependent diffraction lineshape (FWHM)?',
      [
        'FWHM tracks coherence length; its decay reveals phonon dephasing time',
        'FWHM measures the density of phonon modes excited; larger FWHM indicates more modes are thermally populated',
        'FWHM is determined solely by instrumental resolution; ultrafast dynamics appear only in peak shift, not broadening',
        'FWHM encodes the phonon lifetime (linewidth); narrow FWHM indicates long-lived phonon modes',
      ],
      0,
      'TR-XRD peak broadening: FWHM ∝ 1/ξ (coherence length); coherence dephasing from phonon excitation broadens peak; FWHM decay reveals T₂.',
    ],
    [
      'In diffractive X-ray optics for synchrotrons, analyser crystals with asymmetric Bragg geometry (asymmetric cut angle) magnify the angular acceptance compared to symmetric geometry. How does asymmetry improve spectroscopic acceptance without loss of energy resolution?',
      [
        'Asymmetric cuts magnify angular acceptance while preserving Darwin-curve narrowness',
        'Asymmetric cuts reduce the Darwin width by suppressing multiple Bragg reflections',
        'Asymmetric geometry shifts the reflectivity peak away from the Darwin curve, reducing dynamical broadening',
        'Angular magnification arises from the refractive index anisotropy introduced by the cut angle',
      ],
      0,
      'Asymmetric Bragg: Darwin curve angular width = C × symmetric width; magnification C expands acceptance while preserving dynamical narrowness.',
    ],
  ],
};
